import { useEffect, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Navigate,
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
  type NotFoundRouteProps,
} from "@tanstack/react-router";
import AppShell from "@/components/shell/AppShell";
import NotFoundPage from "@/components/shell/NotFoundPage";
import { getOrgBySlug, type UserOrganization } from "@/lib/auth";
import { forgetWorkspace, rememberLastPlace, sectionFromPathname } from "@/lib/last-place";
import { orgSearchSchema } from "@/lib/search-params";
import { trpc } from "@/lib/trpc";
import {
  WindowTitleCompanyContext,
  WindowTitleDetailContext,
  useDocumentTitle,
} from "@/lib/window-title";

// The section and default detail each Company page names in the window title.
const PAGE_TITLES: Record<string, [section: string, detail?: string]> = {
  "media-performance": ["Media Performance"],
  "media-performance/campaigns": ["Media Performance", "By campaign"],
  "media-performance/creatives": ["Media Performance", "By creative"],
  "launch/create": ["Launch", "Create"],
  "launch/edit": ["Launch", "Edit"],
  review: ["Review"],
  audit: ["Audit"],
  settings: ["Settings"],
};

interface OrgLayoutData {
  currentOrg: UserOrganization;
}

export const Route = createFileRoute("/_authenticated/$orgSlug")({
  validateSearch: orgSearchSchema,
  loader: async ({ params, context }): Promise<OrgLayoutData> => {
    // Shares the cache entry the entry route loader already populated
    // (and that `useSuspenseQuery` below reads) — one fetch backs both.
    const user = await context.queryClient.ensureQueryData(trpc.organization.viewer.queryOptions());
    const currentOrg = getOrgBySlug(user, params.orgSlug);
    if (!currentOrg) {
      // Memory is a hint: an inaccessible workspace is forgotten before entry
      // picks again, so `/` can never loop back here.
      forgetWorkspace(params.orgSlug);
      throw redirect({ to: "/", replace: true });
    }
    return { currentOrg };
  },
  component: OrgLayout,
  notFoundComponent: CompanyPageNotFound,
});

// An address the Company does not have (Trends, Audience, an old bookmark)
// opens the Company's default page instead of a bare "Not Found". A page that
// exists but reported not found itself (`throw notFound()`, e.g. Media
// Performance's redirect-loop breaker) says so instead: sending the person
// back to the default page would restart the loop.
function CompanyPageNotFound({ isNotFound }: Partial<NotFoundRouteProps>) {
  const { orgSlug } = Route.useParams();
  if (isNotFound) return <NotFoundPage />;
  return <Navigate to="/$orgSlug" params={{ orgSlug }} replace />;
}

function OrgLayout() {
  const { currentOrg } = Route.useLoaderData();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const pagePath = pathname.slice(`/${currentOrg.slug}/`.length).replace(/\/$/, "");
  const [section, pageDetail] = PAGE_TITLES[pagePath] ??
    PAGE_TITLES[pagePath.split("/")[0]!] ?? [undefined];
  const [detail, setDetail] = useState<string | null>(null);
  const [companyShown, setCompanyShown] = useState(true);
  useDocumentTitle({
    company: companyShown ? currentOrg.name : null,
    section,
    detail: detail ?? pageDetail,
  });
  const { data: viewer } = useSuspenseQuery(trpc.organization.viewer.queryOptions());
  useEffect(() => {
    rememberLastPlace({
      orgSlug: currentOrg.slug,
      section: sectionFromPathname(currentOrg.slug, pathname),
    });
  }, [currentOrg.slug, pathname]);
  return (
    <WindowTitleDetailContext.Provider value={setDetail}>
      <WindowTitleCompanyContext.Provider value={setCompanyShown}>
        <AppShell viewer={viewer} currentOrg={currentOrg}>
          <Outlet />
        </AppShell>
      </WindowTitleCompanyContext.Provider>
    </WindowTitleDetailContext.Provider>
  );
}
